import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useAppSelector } from '../redux/storeConfig/store'
import { useIdentityScope } from '../v2/identity/hooks'
import { identityScopeKeys } from '../v2/identity/queries'
import { featureFlagKeys, featureFlagQueryOptions } from './featureFlagQueries'

const checkoutFlagKey = 'cached_checkout_calculations'
const isCheckoutPath = (pathname: string) => /^\/(cart|checkout|check-out)(\/|$)/.test(pathname)

type StoreCheckoutFlag =
  { status: 'loading' } | { status: 'error'; retry: () => unknown } | { status: 'ready'; enabled: boolean }

export function useStoreCheckoutFlag(location: { pathname: string; search?: string; key?: string }): StoreCheckoutFlag {
  const portalId = useAppSelector(state => state.portal?.id)
  const session = useAppSelector(state => state.session)
  const identity = useIdentityScope()
  const queryClient = useQueryClient()
  const previousToken = useRef(session?.rawToken)

  useEffect(() => {
    if (previousToken.current === session?.rawToken) return
    previousToken.current = session?.rawToken
    queryClient.invalidateQueries({ queryKey: identityScopeKeys.all })
  }, [queryClient, session?.rawToken])

  const matchesSession =
    identity.data?.authenticated === Boolean(session?.rawToken) &&
    (!session?.user_id || identity.data?.user_id === Number(session.user_id))
  const identityKey = matchesSession ? identity.data?.identity_key : undefined
  const active = isCheckoutPath(location.pathname)
  const scope = portalId && identityKey ? JSON.stringify([portalId, identityKey]) : null

  // A flow changes only on entry, exit, or account context change. React retries
  // this render before descendants see a decision from the previous flow.
  const [flow, setFlow] = useState({ active, scope, sequence: 0 })
  const flowChanged = flow.active !== active || (active && flow.scope !== scope)
  if (flowChanged) setFlow({ active, scope, sequence: flow.sequence + 1 })
  const flowSequence = flow.sequence + (flowChanged ? 1 : 0)

  const ready = Boolean(portalId && identityKey)
  const visit = location.key ?? `${location.pathname}${location.search ?? ''}`
  const browsing = useQuery({
    ...featureFlagQueryOptions(
      featureFlagKeys.navigation(portalId ?? 0, identityKey ?? '', checkoutFlagKey, visit),
      checkoutFlagKey
    ),
    enabled: ready && !active,
    staleTime: 0
  })
  const checkout = useQuery({
    ...featureFlagQueryOptions(
      featureFlagKeys.checkout(portalId ?? 0, identityKey ?? '', checkoutFlagKey, flowSequence),
      checkoutFlagKey
    ),
    enabled: ready && active,
    staleTime: 'static'
  })

  if (active && ready && checkout.isSuccess && !checkout.isFetching) {
    return { status: 'ready', enabled: checkout.data }
  }
  if (identity.isError) return { status: 'error', retry: identity.refetch }
  if (!ready) return { status: 'loading' }

  const query = active ? checkout : browsing
  if (query.isError) return { status: 'error', retry: query.refetch }
  if (query.isPending || query.isFetching) return { status: 'loading' }
  return { status: 'ready', enabled: query.data }
}
